import { forwardRef, useState, useContext } from 'react';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import { TabContent, TabPane, Nav, NavItem, NavLink } from 'reactstrap';
// components block
import ActionsLogs from './ActionsLogs';
import ContactEmailNotes from '../Pages/contacts/contact-components/ContactEmailNotes';
import ContactSmsNotes from '../Pages/contacts/contact-components/ContactSmsNotes';
import CandidateEmailNote from '../Pages/candidate/candidate-components/CandidateEmailNote';
import CandidateSmsNotes from '../Pages/candidate/candidate-components/CandidateSmsNotes';
import { CandidateNotesList } from '../Pages/candidate/candidate-components/CandidateNotesList';
// interfaces block
import { CompensationNote } from '../Pages/candidate/candidate-components/CompensationNotes';
import { ParentRef } from '../Pages/agreements/interfaces';
import { AppContext } from '../context';

const ActionLogsAndNotes = forwardRef<ParentRef | undefined,any>(({
  type,
  recordId,
  option,
  haveActionPermissions,
  companyContacts,
  isPrivatePermission = false,
  updateNotesRef,
  candidateOwnerId,
  companyOwnerAndSharees,
},ref) => {
  const [activeTab, setActiveTab] = useState('1');
  const { user, userRoles } = useContext(AppContext);
  const toggle = (tab) => activeTab !== tab && setActiveTab(tab);

  // SMS Notes: executive role only
  const canSeeSmsNotes = !!userRoles?.includes('EXECUTIVE');

  // Email Notes on Contact: executive, or company owner/sharee, or company has no owner
  const companyHasOwner = companyOwnerAndSharees?.some(
    (item: { ownerType: string }) => item.ownerType === 'OWNER'
  );
  const canSeeContactEmailNotes =
    !!userRoles?.includes('EXECUTIVE') ||
    !companyHasOwner ||
    !!companyOwnerAndSharees?.some(
      (item: { owner?: { id: string } }) => item.owner?.id === user?.id
    );

  // Email Notes on Candidate: candidate owner only, or candidate has no owner
  const canSeeCandidateEmailNotes =
    !candidateOwnerId || candidateOwnerId === user?.id;

  const canSeeEmailNotes =
    type === 'Contact' ? canSeeContactEmailNotes : canSeeCandidateEmailNotes;

  return (
    <div className="secondaryNavTabs">
      <Nav tabs className="navTabsOverflow">
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === '1' })}
            onClick={() => {
              toggle('1');
            }}
          >
            Action Logs
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === '2' })}
            onClick={() => {
              toggle('2');
            }}
          >
            {type} Notes
          </NavLink>
        </NavItem>
        {(type === 'Contact' || type === 'Candidate') && canSeeEmailNotes && (
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '3' })}
              onClick={() => {
                toggle('3');
              }}
            >
              {type} Email Notes
            </NavLink>
          </NavItem>
        )}
        {(type === 'Contact' || type === 'Candidate') && canSeeSmsNotes && (
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '5' })}
              onClick={() => {
                toggle('5');
              }}
            >
              {type} SMS Notes
            </NavLink>
          </NavItem>
        )}
        <div className="d-flex align-items-center ml-2">
          <Link
            target="_blank"
            className='nav-link'
            to={{
              pathname: `/notes-es?${new URLSearchParams(option).toString()}`,
            }}
          >
            Note Search
          </Link>
        </div>
        {isPrivatePermission && type === 'Candidate' && (
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '4' })}
              onClick={() => {
                toggle('4');
              }}
            >
              Compensation Notes
            </NavLink>
          </NavItem>
        )}
      </Nav>
      <TabContent activeTab={activeTab}>
        <TabPane tabId="1">
          <div>
            <ActionsLogs
              actionableId={recordId}
              actionableType={type}
              haveActionPersmissions={haveActionPermissions}
            />
          </div>
        </TabPane>
        <TabPane tabId="2">
          <div>
            <CandidateNotesList
              type={type}
              recordId={recordId}
              haveActionPermissions={haveActionPermissions}
              companyContacts={type === 'Company' ? companyContacts : []}
              updateNotesRef={updateNotesRef}
            />
          </div>
        </TabPane>
        {(type === 'Contact' || type === 'Candidate') && canSeeEmailNotes && (
          <TabPane tabId="3">
            <div>
              {type === 'Contact' ? (
                <ContactEmailNotes />
              ) : (
                <CandidateEmailNote recordId={recordId} type={type} />
              )}
            </div>
          </TabPane>
        )}
        {(type === 'Contact' || type === 'Candidate') && canSeeSmsNotes && (
          <TabPane tabId="5">
            <div>
              {type === 'Contact' ? (
                <ContactSmsNotes noteableName={option?.contactName} />
              ) : (
                <CandidateSmsNotes recordId={recordId} noteableName={option?.candidateName} />
              )}
            </div>
          </TabPane>
        )}
        <TabPane tabId="4">
          <div>
            <CompensationNote
              type={type}
              recordId={recordId}
              haveActionPermissions={haveActionPermissions}
              isPrivatePermission={isPrivatePermission}
            />
          </div>
        </TabPane>
      </TabContent>
    </div>
  )
})
export default ActionLogsAndNotes;
